import React, { useState, useEffect, useRef } from 'react';
import DatePickerReact from 'react-datepicker';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { HeaderDatePicker, DateRange, SelectMMYY } from './parts/Parts';
import { Icon, Button, Heading } from '../../Elements/Elements';

import './DatePickerGlobal.module.scss';
import classes from './DatePicker.module.scss';

const DatePicker = ({
  label,
  rangeSection = true,
  calendarSection = true,
  input,
  trigerResetDatePicker,
  minDate,
  maxDate
}) => {
  const [startDate, setStartDate] = useState(
    input.value.startDate === null ? null : new Date(input.value.startDate)
  );
  const [endDate, setEndDate] = useState(
    input.value.endDate === null ? null : new Date(input.value.endDate)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenMMYY, setIsOpenMMYY] = useState(false);
  const [selectDate, setSelectDate] = useState();

  const node = useRef();
  const buttonList = useRef();

  const { t } = useTranslation(['activity']);

  useEffect(() => {
    if (input.value.startDate === null && input.value.endDate === null) {
      setStartDate(null);
      setEndDate(null);
    } else {
      setStartDate(new Date(input.value.startDate));
      setEndDate(new Date(input.value.endDate));
    }
    // eslint-disable-next-line
  }, [trigerResetDatePicker]);

  useEffect(() => {
    if (startDate !== null && endDate !== null) {
      input.onChange({
        startDate: moment(startDate).format('MM/DD/YYYY'),
        endDate: moment(endDate).format('MM/DD/YYYY')
      });
    } else if (startDate === null && endDate === null) {
      input.onChange({ startDate: null, endDate: null });
    }
    // eslint-disable-next-line
  }, [endDate]);

  useEffect(() => {
    setSelectDate(startDate ? startDate : new Date());

    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', escAndTabFunction);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', escAndTabFunction);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  const handleClick = e => {
    if (
      node.current.contains(e.target) ||
      buttonList.current.contains(e.target)
    ) {
      return;
    }
    setIsOpen(false);
    setIsOpenMMYY(false);
  };

  const escAndTabFunction = event => {
    if (event.code === 'Escape' || event.code === 'Tab') {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleClear = () => {
    input.onChange({ startDate: null, endDate: null });
    setStartDate(null);
    setEndDate(null);
  };

  const onChange = dates => {
    const [start, end] = dates;
    setSelectDate(start);
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className={cn(classes.filter)}>
      <label htmlFor={label} size={6} className={classes.label}>
        {label}
      </label>
      <div ref={buttonList}>
        <HeaderDatePicker
          placeholder={t('activity:filters.DATE_PLACEHOLDER')}
          handleOpen={handleOpen}
          handleClear={handleClear}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          open={isOpen}
          id={label}
        />
      </div>
      {isOpen && (
        <div className={classes.bodyDatePicker} ref={node}>
          {!isOpenMMYY ? (
            <>
              {rangeSection && (
                <div className={classes.sectionPicker}>
                  <DateRange
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    startDate={startDate}
                    endDate={endDate}
                    setSelectDate={setSelectDate}
                  />
                </div>
              )}
              {calendarSection && (
                <div className={classes.sectionPicker}>
                  <DatePickerReact
                    minDate={minDate}
                    maxDate={maxDate}
                    inline
                    selected={selectDate ? selectDate : startDate}
                    onChange={onChange}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    renderCustomHeader={props => {
                      const {
                        date,
                        decreaseMonth,
                        increaseMonth,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled
                      } = props;

                      return (
                        <div className={classes.navWrap}>
                          <Button
                            className={classes.buttonMMYY}
                            onClick={() => setIsOpenMMYY(!isOpenMMYY)}
                          >
                            <div className={classes.dateWrap}>
                              <Heading>
                                {`${moment(date).format('MMMM')}  ${moment(
                                  date
                                ).format('YYYY')}`}
                              </Heading>
                              <Icon
                                className={classes.arrow}
                                size="17px"
                                icon={'arrow'}
                                color={'#3a5ba2'}
                              />
                            </div>
                          </Button>

                          <div className={classes.arrowWrap}>
                            <Button
                              className={classes.arrow}
                              onClick={decreaseMonth}
                              disabled={prevMonthButtonDisabled}
                            >
                              <Icon
                                size="17px"
                                icon={'arrow'}
                                color={'#3a5ba2'}
                                style={{
                                  transform: `rotate(${90}deg)`
                                }}
                              />
                            </Button>
                            <Button
                              className={classes.arrow}
                              onClick={increaseMonth}
                              disabled={nextMonthButtonDisabled}
                            >
                              <Icon
                                size="17px"
                                icon={'arrow'}
                                color={'#3a5ba2'}
                                style={{
                                  transform: `rotate(${-90}deg)`
                                }}
                              />
                            </Button>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            <SelectMMYY
              setIsOpenMMYY={setIsOpenMMYY}
              isOpenMMYY={isOpenMMYY}
              startDate={startDate}
              endDate={endDate}
              setSelectDate={setSelectDate}
              selectDate={selectDate}
              minDate={minDate}
              maxDate={maxDate}
            />
          )}
        </div>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  label: PropTypes.string,
  rangeSection: PropTypes.bool,
  calendarSection: PropTypes.bool,
  range: PropTypes.bool,
  input: PropTypes.object.isRequired,
  date: PropTypes.object,
  decreaseMonth: PropTypes.func,
  increaseMonth: PropTypes.func,
  prevMonthButtonDisabled: PropTypes.func,
  nextMonthButtonDisabled: PropTypes.func,
  trigerResetDatePicker: PropTypes.bool,
  minDate: PropTypes.string,
  maxDate: PropTypes.string
};

export default DatePicker;
